/* eslint-disable camelcase */
import { axios, IDynamicFormValue } from 'src/lib';
import {
  FETCH_TOP_N_VALUES,
  FETCH_LOCAL_FILTERS,
  GET_GRID_DATA,
  GET_DRAWER_DATA,
  ADD_NEW_COMMENT,
  UPDATE_COMMENT,
  DELETE_COMMENT,
  SUBMIT_DRAWER_DATA,
  DELETE_DRAWER_COMMENTS,
  SUBMIT_BULK_OPPORTUNITY,
  SUBMIT_BULK_COMMENTS,
  DELETE_ALL_COMMENTS_AND_OPPORTUNITIES,
  GET_PRODUCTS_WITH_COMMENTS,
  GET_LEGENDS,
  GET_OPPORTUNITY_SUMMARY,
  SAVE_SCENARIO_PLANNER,
  GET_SKU_METADATA,
  CREATE_SKU,
  GET_SUMMARY_TABLE_OPPORTUNITIES,
  GET_BULK_INPUTS_TABLE_OPPORTUNITIES,
  DOWNLOAD_PLANNER_OPPORTUNITY_SUMMARY,
  BULK_INPUTS_TABLE_ROWS
} from 'src/constants';
import { IResponseBase } from 'src/types/response-base';
import {
  ICombinationModel,
  IFilterObj,
  IGridMetrics
} from 'src/components/filters/models/filter-response.model';
import { IFieldResponse } from 'src/lib/dynamic-forms/types/control-field.base';
import { IGridResponsePayload } from '../types/grid-response-payload';
import { IGridXToYResponse } from '../types/grid-x-to-y-response.model';
import { IDrawerPayload } from '../types/drawer-view-type';
import {
  IOpportunity,
  IProductComment,
  IProductDetails
} from '../types/ppg-sku-drawer.model';
import { IOpportunityPayload } from '../types/opportunity.model';
import { ITopNBrandResponse } from '../types/local-filters-model';
import {
  IBulkCommentsPayload,
  IBulkInputsTableData,
  IOpportunitySummaryTableData
} from '../types/bulk-inputs-table-data.model';
import { IBulkCommentsDrawerResponse } from '../types/bulk-comment-drawer';
import { ProductSelection } from '../../constants/constant';

// api call for fetching local filters
export function fetchLocalFilters(
  sourceID: number,
  scenarioId: number,
  filters: IFilterObj[]
) {
  return axios
    .post(`${FETCH_LOCAL_FILTERS}${sourceID || 1}/${scenarioId}/`, {
      filter_inputs: filters
    })
    .then<{ localFilter: IFieldResponse[] }>((response) => response.data)
    .then((response) => {
      response.localFilter.sort(
        (a, b) => a.placement_order - b.placement_order
      );
      return response;
    });
}

// api call for fetching top n brands
export function fetchTopNBrands(
  source_id: number,
  module_id: number,
  scenario_id: number,
  filter_changed: number,
  filter_inputs: object[]
) {
  const payload = {
    filter_changed,
    filter_inputs
  };
  return axios
    .post(
      `${FETCH_TOP_N_VALUES}${source_id || 1}/${module_id || 1}/${
        scenario_id || 1
      }/`,
      payload
    )
    .then<IResponseBase<ITopNBrandResponse[]>>((response) => response.data);
}

// api for getting planner grid data as per all the filter values
export function fetchGridData(
  moduleId: number,
  sourceId: number,
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: IGridResponsePayload = {
    filter_inputs: filterInputs,
    local_filters: localFilters
  };
  return axios
    .post(`${GET_GRID_DATA}${sourceId}/${moduleId}/${scenarioId}/`, payload)
    .then<IResponseBase<IGridXToYResponse>>((response) => response.data);
}

// api call for fetching drawer data as per ppg/sku view
export function fetchDrawerData(
  payload: IDrawerPayload,
  sourceId: number,
  moduleId: number,
  scenarioId: number
) {
  return axios
    .post(`${GET_DRAWER_DATA}${sourceId}/${moduleId}/${scenarioId}/`, payload)
    .then<IResponseBase<IProductDetails>>((response) => response.data);
}

export function submitDrawerOpportunities(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  payload: IOpportunityPayload[],
  filters: IFilterObj[]
) {
  return axios.post(
    `${SUBMIT_DRAWER_DATA}${sourceId}/${moduleId}/${scenarioId}/`,
    { prod_details: payload, filter_inputs: filters }
  );
}

export function deleteProductCommentsAndOpportunities(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  payload: {
    ppg_id: number;
    sku_id: number;
    toggle: string;
    reset_all: boolean;
    filter_inputs: IFilterObj[];
  }
) {
  return axios.delete(
    `${DELETE_DRAWER_COMMENTS}${sourceId}/${moduleId}/${scenarioId}/`,
    {
      data: payload
    }
  );
}

export function deleteCommentsAndOpportunitiesForAllProduct(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  payload: {
    prod_details: { ppg_id: number; sku_id: number }[];
    toggle: string;
    reset_all: boolean;
    filter_inputs: IFilterObj[];
  }
) {
  return axios.delete(
    `${DELETE_ALL_COMMENTS_AND_OPPORTUNITIES}${sourceId}/${moduleId}/${scenarioId}/`,
    {
      data: payload
    }
  );
}

// api for create comment
export function addComment(
  payload: {
    ppg_id?: number;
    sku_id?: number;
    comment_text: string;
    filter_inputs?: IFilterObj[];
    is_scenario_level?: boolean; 
  },
  scenarioId: number
) {
  return axios
    .post(`${ADD_NEW_COMMENT}${scenarioId}/`, payload)
    .then<IResponseBase<IProductComment>>((response) => response.data);
}

// api for edit comment
export function editComment(payload: {
  commentId: number;
  commentText: string;
}) {
  return axios
    .patch(`${UPDATE_COMMENT}${payload.commentId}/`, {
      comment_text: payload.commentText
    })
    .then<IResponseBase<IProductComment>>((response) => response.data);
}

// api for delete comment
export function deleteComment(commentId: number) {
  return axios
    .delete(`${DELETE_COMMENT}${commentId}/`)
    .then((response) => response.data);
}

export function fetchOpportunities(sourceId: number) {
  return axios
    .get<{ opportunities: IProductDetails['opportunities'] }>(
      `${GET_LEGENDS}${sourceId}/`
    )
    .then((response) => response.data.opportunities);
}

export function submitBulkOpportunities(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  payload: IOpportunityPayload[],
  filters: IFilterObj[]
) {
  return axios.post(
    `${SUBMIT_BULK_OPPORTUNITY}${sourceId}/${moduleId}/${scenarioId}/`,
    {
      prod_details: payload,
      filter_inputs: filters
    }
  );
}

export function submitBulkComment(
  scenarioId: number,
  payload: IBulkCommentsPayload
) {
  return axios
    .post(`${SUBMIT_BULK_COMMENTS}${scenarioId}/`, payload)
    .then<IResponseBase<IProductComment>>((response) => response.data);
}

export function fetchProductsWithComments(
  moduleId: number,
  sourceId: number,
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: IGridResponsePayload = {
    filter_inputs: filterInputs,
    local_filters: localFilters
  };
  return axios
    .post(
      `${GET_PRODUCTS_WITH_COMMENTS}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IBulkCommentsDrawerResponse[]>>(
      (response) => response.data
    );
}

export function fetchOpportunitySummaryDetails(
  moduleId: number,
  sourceId: number,
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: IGridResponsePayload = {
    filter_inputs: filterInputs,
    local_filters: localFilters
  };
  return axios
    .post(
      `${GET_OPPORTUNITY_SUMMARY}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IOpportunitySummaryTableData[]>>(
      (response) => response.data
    );
}

export function saveScenarioPlanner(
  scenarioId: number,
  filterInputs: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload: { filter_inputs: IFilterObj[]; local_filters: string } = {
    filter_inputs: filterInputs,
    local_filters: JSON.stringify(localFilters)
  };
  return axios
    .post(`${SAVE_SCENARIO_PLANNER}${scenarioId}/`, payload)
    .then<IResponseBase<IOpportunitySummaryTableData[]>>(
      (response) => response.data
    );
}
export function fetchAddSKUFormFields(
  sourceId: number,
  scenarioId: number,
  moduleId: number,
  filters: IFilterObj[]
) {
  return axios
    .post(`${GET_SKU_METADATA}${sourceId}/${moduleId}/${scenarioId}/`, {
      filter_inputs: filters
    })
    .then<{ combinations: ICombinationModel[]; fields: IFieldResponse[] }>(
      (response) => response.data
    )
    .then<{ combinations: ICombinationModel[]; fields: IFieldResponse[] }>(
      (response) => ({
        fields: response.fields
          // .filter((field) => field.planner_flag)
          .sort((a, b) => a.placement_order - b.placement_order),
        combinations: response.combinations
      })
    );
}

export function createSKU(
  sourceId: number,
  scenarioId: number,
  moduleId: number,
  payload: IDynamicFormValue,
  filters: IFilterObj[]
) {
  return axios.post(`${CREATE_SKU}${sourceId}/${moduleId}/${scenarioId}/`, {
    new_sku_inputs: [payload],
    filter_inputs: filters
  });
}

export function fetchOpportunityForSummary(sourceId: number) {
  return axios
    .get(`${GET_SUMMARY_TABLE_OPPORTUNITIES}${sourceId}/`)
    .then<{ opportunities: { ppg: IOpportunity[]; sku: IOpportunity[] } }>(
      (response) => response.data
    );
}

export function fetchOpportunityForBulkInputsTable(sourceId: number) {
  return axios
    .get(`${GET_BULK_INPUTS_TABLE_OPPORTUNITIES}${sourceId}/`)
    .then<{ opportunities: { ppg: IOpportunity[]; sku: IOpportunity[] } }>(
      (response) => response.data
    );
}

export function downloadOpportunitySummary(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  scenarioName: string,
  filters: IFilterObj[],
  localFilters: IGridMetrics
) {
  const payload = {
    filter_inputs: filters,
    local_filters: localFilters
  };
  return axios
    .post(
      `${DOWNLOAD_PLANNER_OPPORTUNITY_SUMMARY}${sourceId}/${moduleId}/${scenarioId}/`,
      payload,
      {
        responseType: 'blob'
      }
    )
    .then((response) => {
      const href = URL.createObjectURL(response.data);
      // create "a" HTML element with href to file & click
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', `${scenarioName}.xlsx`);
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
      return {
        data: 'Success'
      };
    });
}

export function fetchBulkInputsTableRows(
  sourceId: number,
  moduleId: number,
  scenarioId: number,
  filters: IFilterObj[],
  localFilters: IGridMetrics,
  toggleView: string
) {
  const payload = {
    filter_inputs: filters,
    local_filters: localFilters
  };
  return axios
    .post(
      `${BULK_INPUTS_TABLE_ROWS}${sourceId}/${moduleId}/${scenarioId}/`,
      payload
    )
    .then<IResponseBase<IBulkInputsTableData[]>>((response) => response.data)
    .then((response) => {
      if (toggleView === ProductSelection.sku) {
        // eslint-disable-next-line no-param-reassign
        response.data = response.data
          .map((row) => row.subRows)
          .flat(1)
          .map((row, index) => ({ ...row, itemIndex: index, subRows: [] }));

        return response;
      }
      // eslint-disable-next-line no-param-reassign
      response.data = response.data.map((row, index) => ({
        ...row,
        itemIndex: index,
        subRows: row.subRows.map((subRow, subRowIndex) => ({
          ...subRow,
          itemIndex: subRowIndex
        }))
      }));
      return response;
    });
}
